import {FC, useEffect, useMemo, useState} from 'react';
import {COVER_FEE_TEXT, OUTLINED, TOP} from '@/utils/constants';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  Drawer,
  Tooltip,
} from '@mui/material';
import {ReactComponent as QuestionIcon} from '@icons/question.svg';
import Button from '@/shared/components/Button';
import {contributions} from './AttendeeContributionTabDetails.styles';
import {AttendeeDetailsPageStyle} from '@/pages/Organization/AttendeeDetails/AttendeeDetails.styles';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getAttendeeSelector} from '@/store/organization-attendees';
import {getAttendeesContributions, getAttendeesContributionSelector} from '@/store/organization-attendeeContribution';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import Modal from '@/shared/components/Modal';
import PaymentModal from '../PaymentModal';
import {ITeamRole, PAYMENT_METHOD} from '@/utils/enums';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import {currencyFormatter} from '@/utils/helpers/currency';
import Checkbox from '@/shared/components/Checkbox';
import {calculateFees} from '@/utils/helpers/feeCalculations';
import {orgAPI} from '@/core/api';
import {getCurrentEventSelector} from '@/store/organization-event';
import {OrganizationStripeService} from '@/utils/services/organization-stripe.services';
import {getTeamSelector} from '@/store/organization';

const AttendeeContributionTabDetails: FC = () => {
  const dispatch = useAppDispatch();

  const attendeeContributions = useAppSelector(getAttendeesContributionSelector);
  const currentEvent = useAppSelector(getCurrentEventSelector);
  const attendee = useAppSelector(getAttendeeSelector);
  const team = useAppSelector(getTeamSelector);

  const isStandartMember = !!team && team.role === ITeamRole.STANDART;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [paymentOption, setPaymentOption] = useState<string>(PAYMENT_METHOD.CREDIT_CARD);
  const [isCoverFee, setIsCoverFee] = useState(true);

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const getContributionsData = async () => {
    if (!attendee) return;
    try {
      await dispatch(getAttendeesContributions({attendeeId: attendee?.id, apiInstance: orgAPI})).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const sendPaymentLink = async () => {
    if (!currentEvent || !attendee) return;

    try {
      await OrganizationStripeService.sendPaymentLink(attendee?.id, currentEvent?.id);
      toast.success('Payment link sent successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleMakePayment = async () => {
    if (!attendee || !currentEvent) return;

    try {
      if (paymentOption === PAYMENT_METHOD.CREDIT_CARD) {
        const {data} = await OrganizationStripeService.paymentOrgStripe(attendee?.id, {
          isCoverFee,
          eventId: currentEvent?.id,
        });

        if (data?.url) {
          window.location.href = data.url;
        }
      } else if (PAYMENT_METHOD.CASH_OR_CHECK) {
        await OrganizationStripeService.makePaidOrg(attendee?.id, {eventId: currentEvent?.id});
        await getContributionsData();
        toggleModal();
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const remainingBalance = useMemo(() => {
    const total = Number(attendeeContributions.totalPrice);
    const paid = Number(attendeeContributions.totalPaid);
    if (Number.isNaN(total) || Number.isNaN(paid)) return 0;
    return Math.max(0, total - paid);
  }, [attendeeContributions.totalPrice, attendeeContributions.totalPaid]);

  const fees = useMemo(
    () => calculateFees(remainingBalance, isCoverFee, currentEvent?.organization?.paymentSettings?.transactionFee ?? 0),
    [remainingBalance, isCoverFee, currentEvent?.organization?.paymentSettings?.transactionFee]
  );

  const feePaid = useMemo(() => {
    const paidSumWithoutFee = attendeeContributions.contributions.reduce((acc, item) => {
      return item.isPaid ? acc + item.price : acc;
    }, 0);
    const res = attendeeContributions.totalPaid - paidSumWithoutFee;

    return Number.isNaN(res) ? 0 : res;
  }, [attendeeContributions.contributions, attendeeContributions.totalPaid]);

  const totalContributionsSum = useMemo(() => {
    const itemsSum = attendeeContributions.contributions.reduce((acc, item) => acc + item.price, 0);
    return itemsSum + (isCoverFee ? fees.coverFeeLine : 0);
  }, [attendeeContributions.contributions, fees.coverFeeLine, isCoverFee]);

  const toPay = useMemo(() => Number(fees.amountChargedToPatron), [fees]);

  const disabledButton = toPay === 0;

  useEffect(() => {
    getContributionsData();
  }, [attendee?.id]);

  useEffect(() => {
    if (paymentOption === PAYMENT_METHOD.CASH_OR_CHECK) {
      setIsCoverFee(false);
    } else {
      setIsCoverFee(true);
    }
  }, [paymentOption]);

  return (
    <Box sx={contributions.wrapper}>
      <Box sx={{...(isStandartMember && {opacity: 0.5, pointerEvents: 'none'})}}>
        <Box
          sx={
            attendeeContributions.contributions && attendeeContributions.contributions.length > 0
              ? contributions.tableWrap
              : {}
          }
        >
          <TableContainer>
            <Table sx={contributions.table} aria-label="enhanced table">
              <colgroup>
                <col style={{width: '52%'}} />
                <col style={{width: '16%'}} />
                <col style={{width: '16%'}} />
                <col style={{width: '16%'}} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>
                    <p style={contributions.headText}>Quantity</p>
                  </TableCell>
                  <TableCell>
                    <p style={contributions.headText}>Paid</p>
                  </TableCell>
                  <TableCell>
                    <p style={contributions.headText}>Total</p>
                  </TableCell>
                </TableRow>
              </TableHead>

              {attendeeContributions.contributions && attendeeContributions.contributions.length > 0 ? (
                <TableBody>
                  {attendeeContributions.contributions &&
                    attendeeContributions.contributions.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell sx={contributions.tableCell}>
                          <Box sx={contributions.tableCell}>
                            <p style={contributions.contribution_name}>{row.name}</p>
                            {row.category && <p style={contributions.neutral300}>{row.category}</p>}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={contributions.tableCell}>
                            <p style={contributions.cellText}>{row.amount}</p>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={contributions.tableCell}>
                            <p style={contributions.cellText}>
                              {row.isPaid ? currencyFormatter.format(row.price) : '$0'}
                            </p>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={contributions.tableCell}>
                            <p style={contributions.cellText}>{currencyFormatter.format(row.price)}</p>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}

                  <TableRow>
                    <TableCell sx={contributions.tableCell}>
                      <Box sx={contributions.coverFeeTitle}>
                        <Checkbox value={isCoverFee} onChange={() => setIsCoverFee(!isCoverFee)} />
                        <p style={contributions.contribution_name}>Cover fee</p>

                        <Tooltip title={`${COVER_FEE_TEXT} info`} placement={TOP}>
                          <Box sx={contributions.coverFeeTooltip}>
                            <QuestionIcon />
                          </Box>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell />
                    <TableCell>
                      <Box sx={contributions.tableCell}>
                        <p style={contributions.cellText}>{currencyFormatter.format(feePaid)}</p>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={contributions.tableCell}>
                        <p style={contributions.cellText}>
                          {isCoverFee ? currencyFormatter.format(fees.coverFeeLine) : currencyFormatter.format(0)}
                        </p>
                      </Box>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={contributions.tableCell}>
                      <Box sx={contributions.tableCell}>
                        <p style={contributions.totalRowName}>Total contributions</p>
                      </Box>
                    </TableCell>
                    <TableCell />
                    <TableCell>
                      <Box sx={contributions.tableCell}>
                        <p style={contributions.totalText}>
                          {currencyFormatter.format(attendeeContributions.totalPaid)}
                        </p>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={contributions.tableCell}>
                        <p style={contributions.totalText}>{currencyFormatter.format(totalContributionsSum)}</p>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableRow>
                  <TableCell colSpan={4} sx={contributions.empty}>
                    <EmptyTableData />
                  </TableCell>
                </TableRow>
              )}
            </Table>
          </TableContainer>
        </Box>
        {attendeeContributions.contributions && attendeeContributions.contributions.length > 0 && (
          <Box sx={contributions.toPayWrap}>
            <Typography sx={contributions.toPayText}>Amount Due</Typography>
            <Typography sx={contributions.toPayText}>{currencyFormatter.format(toPay)}</Typography>
          </Box>
        )}
      </Box>

      <Drawer variant="permanent" anchor="bottom" sx={AttendeeDetailsPageStyle.drawer}>
        <Box sx={AttendeeDetailsPageStyle.buttonWrapper}>
          <Button
            title="Send Payment Link"
            variant={OUTLINED}
            disabled={disabledButton || isStandartMember}
            onClick={sendPaymentLink}
          />
          <Button title="Go to Payment" onClick={toggleModal} disabled={disabledButton || isStandartMember} />
        </Box>
      </Drawer>

      <Box sx={contributions.buttonWrap}>
        <Button
          title="Send Payment Link"
          variant={OUTLINED}
          disabled={disabledButton || isStandartMember}
          onClick={sendPaymentLink}
        />
        <Button title="Go to Payment" disabled={disabledButton || isStandartMember} onClick={toggleModal} />
      </Box>

      <Modal
        onClose={toggleModal}
        open={isModalOpen}
        sx={AttendeeDetailsPageStyle.modal}
        title="Go To Payment"
        mainButtonTitle="Confirm"
        closeButtonTitle="Cancel"
        isShowCloseButton
        isShowSaveButton
        onSave={handleMakePayment}
      >
        <PaymentModal toPay={toPay} paymentOption={paymentOption} setPaymentOption={setPaymentOption} />
      </Modal>
    </Box>
  );
};

export default AttendeeContributionTabDetails;
